import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Lock, Mail, RefreshCw, Search, Send, TriangleAlert, Undo2 } from 'lucide-react';
import {
  CLASSIFICATION_LABEL,
  Classification,
  PERIOD_STATUS_LABEL,
  PeriodStatus,
  UserRole,
  type EmailListDto,
  type EmailMessageDto,
  type PeriodPreviewDto,
  type RankingDto,
  type RankingRowDto,
  type UnmeasuredIndexDto,
} from '@weloze/shared';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePageLabel } from '../../lib/navigation';
import { formatDateTime, formatIndex } from '../../lib/format';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  Textarea,
  cn,
} from '../../components/ui';

/**
 * `/admin/competencias/:year/:month` — pré-visualizar, fechar, publicar,
 * reabrir e recalcular (PLAN §7, tela 12), e o ranking da competência (tela 8).
 *
 * As duas telas são a mesma de propósito: o ranking de um mês fechado é a mesma
 * tabela que a pré-visualização mostra antes de fechar. Separá-las produziria
 * duas versões da mesma lista, com risco de divergirem — e a conferência antes
 * de fechar deixaria de conferir a tela que vale.
 */

const TONE: Record<Classification, 'success' | 'warning' | 'danger' | 'neutral'> = {
  EXCELLENT: 'success',
  GOOD: 'warning',
  UNSATISFACTORY: 'danger',
  NOT_APPLICABLE: 'neutral',
};

type Row = Pick<
  RankingRowDto,
  'storeId' | 'protheusRef' | 'supplierName' | 'idc' | 'iqa' | 'isq' | 'iqw' | 'classification'
> & {
  position?: number | null;
  alteredAfterPublication?: boolean;
  unmeasured: UnmeasuredIndexDto[];
};

/**
 * O filtro de classificação.
 *
 * O padrão é **"Todas"**. Era "Com avaliação" enquanto mais da metade das lojas
 * ficava sem nota num mês típico; desde a decisão de 08/2026 (PLAN §3.5) todas
 * são avaliadas, e esconder alguém por padrão passou a esconder informação.
 *
 * "Índice sem apuração" continua sendo uma pergunta legítima — só que agora é
 * sobre **sobre o que a nota foi calculada**, não sobre quem tem nota.
 */
type Filtro = 'ALL' | Classification | 'UNMEASURED';

const FILTRO_LABEL: Record<Filtro, string> = {
  ALL: 'Todas',
  EXCELLENT: 'Excelente',
  GOOD: 'Bom',
  UNSATISFACTORY: 'Insatisfatório',
  NOT_APPLICABLE: 'Não aplicável (regra antiga)',
  UNMEASURED: 'Com índice sem apuração',
};

function combina(row: Row, filtro: Filtro, busca: string): boolean {
  if (filtro === 'UNMEASURED' && row.unmeasured.length === 0) return false;
  if (filtro !== 'ALL' && filtro !== 'UNMEASURED' && row.classification !== filtro) return false;

  const termo = busca.trim().toLowerCase();
  if (termo === '') return true;
  return (
    row.supplierName.toLowerCase().includes(termo) || row.protheusRef.toLowerCase().includes(termo)
  );
}

export function PeriodDetailPage() {
  const { year, month } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.WELOZE_ADMIN;

  const [preview, setPreview] = useState<PeriodPreviewDto | null>(null);
  const [ranking, setRanking] = useState<RankingDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'reopen' | 'recalculate' | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('ALL');
  const [busca, setBusca] = useState('');

  const base = `/periods/${year}/${month}`;

  const load = useCallback(async () => {
    setError(null);
    try {
      const period = await api.get<PeriodPreviewDto['period']>(base);
      if (period.status === PeriodStatus.OPEN) {
        setRanking(null);
        setPreview(await api.get<PeriodPreviewDto>(`${base}/preview`));
      } else {
        setPreview(null);
        setRanking(await api.get<RankingDto>(`${base}/ranking`));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar a competência.');
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  const period = preview?.period ?? ranking?.period ?? null;
  usePageLabel(period?.label ?? null);

  async function act(path: string, body: unknown, label: string) {
    setBusy(label);
    setError(null);
    try {
      await api.post(`${base}/${path}`, body);
      setDialog(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível concluir a operação.');
    } finally {
      setBusy(null);
    }
  }

  if (!period) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-24" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  // Os dois caminhos convergem para o mesmo formato: o ranking traz o motivo do
  // N/A direto, e a pré-visualização o carrega dentro da memória de cálculo.
  const rows: Row[] =
    ranking?.rows ??
    preview?.results.map((result) => ({
      ...result,
      unmeasured: result.calculation.unmeasured,
    })) ??
    [];

  const semApuracao = rows.filter((row) => row.unmeasured.length > 0);
  const visiveis = rows.filter((row) => combina(row, filtro, busca));

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{period.label}</h1>
            <Badge
              tone={
                period.status === PeriodStatus.PUBLISHED
                  ? 'success'
                  : period.status === PeriodStatus.CLOSED
                    ? 'warning'
                    : 'neutral'
              }
            >
              {PERIOD_STATUS_LABEL[period.status]}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {period.status === PeriodStatus.OPEN ? (
              <>Pré-visualização: é exatamente o que o fechamento gravaria. Nada foi salvo ainda.</>
            ) : period.status === PeriodStatus.CLOSED ? (
              <>
                Fechada em {formatDateTime(period.closedAt)}
                {period.closedByName && ` por ${period.closedByName}`}. O fornecedor ainda não
                enxerga estes números.
              </>
            ) : (
              <>
                Publicada em {formatDateTime(period.publishedAt)}
                {period.publishedByName && ` por ${period.publishedByName}`}.
              </>
            )}
          </p>
        </div>

        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            {period.status === PeriodStatus.OPEN && (
              <Button
                loading={busy === 'close'}
                onClick={() => void act('close', { force: !period.isComplete }, 'close')}
              >
                <Lock className="size-4" aria-hidden />
                {period.isComplete ? 'Fechar competência' : 'Fechar mesmo assim'}
              </Button>
            )}
            {period.status === PeriodStatus.CLOSED && (
              <>
                <Button variant="outline" onClick={() => setDialog('reopen')}>
                  <Undo2 className="size-4" aria-hidden />
                  Reabrir
                </Button>
                <Button loading={busy === 'publish'} onClick={() => void act('publish', {}, 'publish')}>
                  <Send className="size-4" aria-hidden />
                  Publicar
                </Button>
              </>
            )}
            {period.status === PeriodStatus.PUBLISHED && (
              <Button variant="outline" onClick={() => setDialog('recalculate')}>
                <RefreshCw className="size-4" aria-hidden />
                Recalcular
              </Button>
            )}
          </div>
        )}
      </header>

      {period.status === PeriodStatus.PUBLISHED && period.id && (
        <PublicationNotice periodId={period.id} count={period.evaluationCount} isAdmin={isAdmin} />
      )}

      {period.status === PeriodStatus.OPEN && !period.isComplete && (
        <Alert>
          {period.label} ainda não terminou. Fechar agora congela um número que as entregas do
          restante do mês mudariam.
        </Alert>
      )}

      {period.hasAlterations && (
        <Alert>
          <span className="flex items-center gap-2">
            <TriangleAlert className="size-4 shrink-0" aria-hidden />
            Competência alterada após a publicação. As avaliações recalculadas aparecem marcadas na
            tabela, e a versão anterior foi preservada.
          </span>
        </Alert>
      )}

      {error && <Alert>{error}</Alert>}

      {preview && <Summary summary={preview.summary} version={preview.parameterSetVersion} />}

      {rows.length === 0 ? (
        <EmptyState
          title="Nenhuma loja monitorada nesta competência"
          description="Só entram lojas com monitoramento ligado e competência a partir do início do monitoramento."
        />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <Field
              label="Fornecedor"
              name="busca"
              className="w-72"
              placeholder="Nome ou código"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              icon={<Search className="size-4" aria-hidden />}
            />
            <Select
              label="Classificação"
              name="filtro"
              className="w-56"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value as Filtro)}
            >
              {(Object.keys(FILTRO_LABEL) as Filtro[]).map((value) => (
                <option key={value} value={value}>
                  {FILTRO_LABEL[value]}
                </option>
              ))}
            </Select>
            <p className="pb-2 text-sm text-slate-500">
              {visiveis.length} de {rows.length}
            </p>
          </div>

          {visiveis.length === 0 ? (
            <EmptyState
              title="Nenhum fornecedor neste filtro"
              description="Troque a classificação ou limpe a busca."
            />
          ) : (
            <ResultsTable
              rows={visiveis}
              published={period.status === PeriodStatus.PUBLISHED}
              ranked={period.status !== PeriodStatus.OPEN}
              year={period.year}
              month={period.month}
            />
          )}
        </>
      )}

      {semApuracao.length > 0 && <UnmeasuredTable rows={semApuracao} />}

      <ReasonDialog
        kind={dialog}
        busy={busy !== null}
        onClose={() => setDialog(null)}
        onConfirm={(reason) =>
          void act(dialog === 'reopen' ? 'reopen' : 'recalculate', { reason }, dialog ?? '')
        }
      />
    </div>
  );
}

function Summary({
  summary,
  version,
}: {
  summary: PeriodPreviewDto['summary'];
  version: number;
}) {
  const cards = [
    { label: 'Lojas monitoradas', value: String(summary.stores) },
    { label: 'Com avaliação', value: String(summary.evaluated) },
    { label: 'Índice sem apuração', value: String(summary.unmeasured) },
    { label: 'IQW médio', value: formatIndex(summary.averageIqw) },
  ];

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => (
          <Card key={card.label}>
            <p className="text-xs uppercase tracking-wide text-slate-500">{card.label}</p>
            <p className="mt-1 text-2xl font-semibold text-slate-900">{card.value}</p>
          </Card>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
        <Badge tone="success">{summary.excellent} Excelente</Badge>
        <Badge tone="warning">{summary.good} Bom</Badge>
        <Badge tone="danger">{summary.unsatisfactory} Insatisfatório</Badge>
        <span className="text-xs text-slate-500">
          calculado com o conjunto de parâmetros v{version}
        </span>
      </div>
    </div>
  );
}

function ResultsTable({
  rows,
  published,
  ranked,
  year,
  month,
}: {
  rows: Row[];
  published: boolean;
  /** A posição só existe depois do fechamento — antes disso a coluna some. */
  ranked: boolean;
  year: number;
  month: number;
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full min-w-[52rem] text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
            {ranked && <th className="px-4 py-3 font-medium">#</th>}
            <th className="px-4 py-3 font-medium">Fornecedor</th>
            <th className="px-4 py-3 text-right font-medium">IDC</th>
            <th className="px-4 py-3 text-right font-medium">IQA</th>
            <th className="px-4 py-3 text-right font-medium">ISQ</th>
            <th className="px-4 py-3 text-right font-medium">IQW</th>
            <th className="px-4 py-3 font-medium">Classificação</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => (
            <tr key={row.storeId} className="transition-colors hover:bg-slate-50/70">
              {ranked && (
                <td className="px-4 py-3 tabular-nums text-slate-400">{row.position ?? '—'}</td>
              )}
              <td className="px-4 py-3">
                <Link
                  to={`/relatorio/${row.storeId}/${year}/${month}`}
                  className="font-medium text-brand-700 underline-offset-4 hover:underline"
                >
                  {row.supplierName}
                </Link>
                <p className="font-mono text-xs text-slate-500">{row.protheusRef}</p>
                {row.alteredAfterPublication && (
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-red-600">
                    <TriangleAlert className="size-3" aria-hidden />
                    recalculada após a publicação
                  </p>
                )}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                {formatIndex(row.idc)}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                {formatIndex(row.iqa)}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                {formatIndex(row.isq)}
              </td>
              <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900">
                {formatIndex(row.iqw)}
              </td>
              <td className="px-4 py-3">
                <Badge tone={TONE[row.classification]}>
                  {CLASSIFICATION_LABEL[row.classification]}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!published && (
        <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
          Estes números ainda não estão visíveis para os fornecedores.
        </p>
      )}
    </div>
  );
}

/**
 * As lojas que não geraram avaliação, com o motivo.
 *
 * Fica em tabela própria porque a informação é outra: aqui não há nota a
 * comparar, e sim uma explicação a ler. Misturá-las na tabela de cima encheria
 * as colunas de índice com travessões.
 *
 * Aparece nos dois estados da competência — na pré-visualização e depois de
 * fechada. Antes só existia na pré-visualização, e a competência fechada
 * escondia justamente o que ela precisa explicar.
 */
/**
 * As lojas com algum índice sem apuração.
 *
 * A tabela mudou de sentido em 08/2026: antes listava quem **não tinha nota**,
 * agora lista quem **tem nota calculada sobre menos de três índices**. É uma
 * conferência antes de fechar, e não uma lista de excluídos — por isso mostra
 * qual índice faltou e o porquê, e não só o nome do fornecedor.
 */
function UnmeasuredTable({ rows }: { rows: Row[] }) {
  const linhas = rows.flatMap((row) =>
    row.unmeasured.map((index) => ({ row, index })),
  );

  return (
    <Card>
      <h2 className="font-medium text-slate-900">
        {rows.length === 1
          ? '1 loja com índice sem apuração nesta competência'
          : `${rows.length} lojas com índice sem apuração nesta competência`}
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        Elas continuam avaliadas: o índice sem dado não é pontuado, e o IQW sai dos demais com os
        pesos redistribuídos. Não é nota zero nem nota cheia.
      </p>

      <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full min-w-[40rem] text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/70 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2 font-medium">Fornecedor</th>
              <th className="px-3 py-2 font-medium">Código</th>
              <th className="px-3 py-2 font-medium">Índice</th>
              <th className="px-3 py-2 font-medium">Motivo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {linhas.map(({ row, index }) => (
              <tr
                key={`${row.storeId}-${index.index}`}
                className="transition-colors hover:bg-slate-50/70"
              >
                <td className="px-3 py-2">
                  <Link
                    to={`/admin/fornecedores/${row.storeId}`}
                    className="font-medium text-brand-700 underline-offset-4 hover:underline"
                  >
                    {row.supplierName}
                  </Link>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{row.protheusRef}</td>
                <td className="px-3 py-2">
                  <Badge tone="neutral">{index.index}</Badge>
                </td>
                <td className="px-3 py-2 text-slate-600">{index.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/**
 * Reabrir e recalcular exigem motivo pelo mesmo motivo: os dois mudam um número
 * que alguém já conferiu, e quem ler o `audit_log` daqui a dois anos precisa
 * encontrar a explicação junto do fato.
 */
function ReasonDialog({
  kind,
  busy,
  onClose,
  onConfirm,
}: {
  kind: 'reopen' | 'recalculate' | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    setReason('');
  }, [kind]);

  const reopening = kind === 'reopen';

  return (
    <Modal
      open={kind !== null}
      onClose={onClose}
      title={reopening ? 'Reabrir competência' : 'Recalcular competência'}
      description={
        reopening
          ? 'As avaliações desta competência serão apagadas e o mês volta a ficar aberto. Só é possível porque ela ainda não foi publicada.'
          : 'O recálculo cria uma nova versão de cada avaliação, preserva a anterior e marca a competência como alterada após a publicação.'
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          onConfirm(reason);
        }}
      >
        <Textarea
          label="Motivo"
          name="reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          hint={
            reopening
              ? 'Fica no registro de auditoria — é o que explica o mês ter voltado atrás.'
              : 'Aparece na tela e no relatório do fornecedor, junto do selo de alteração.'
          }
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={busy} disabled={reason.trim().length < 10}>
            {reopening ? 'Reabrir' : 'Recalcular'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Quantos fornecedores souberam da publicação.
 *
 * Publicar dispara um e-mail por loja, em sequência e em segundo plano. Um
 * restart no minuto seguinte — um deploy, por exemplo — deixaria metade da
 * carteira sem saber, e ninguém perceberia: a competência ficaria publicada, e o
 * fornecedor esperando um aviso que nunca saiu.
 *
 * Reenviar é seguro: o e-mail diz a nota, e a nota não mudou.
 */
function PublicationNotice({
  periodId,
  count,
  isAdmin,
}: {
  periodId: string;
  count: number;
  isAdmin: boolean;
}) {
  const [avisados, setAvisados] = useState<number | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      const lista = await api.get<EmailListDto>(
        `/mail?periodId=${periodId}&kind=PERIOD_PUBLISHED&limit=200`,
      );
      setAvisados(lista.messages.filter((m: EmailMessageDto) => m.status !== 'FAILED').length);
    } catch {
      setAvisados(null);
    }
  }, [periodId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (avisados === null) return null;

  const completo = avisados >= count;

  async function reenviar() {
    setEnviando(true);
    setErro(null);
    try {
      await api.post(`/mail/period/${periodId}`);
      await carregar();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível reenviar.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="print:hidden">
      <p
        className={cn(
          'flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border px-3 py-2 text-xs',
          completo
            ? 'border-slate-200 bg-slate-50/60 text-slate-600'
            : 'border-amber-200 bg-amber-50/60 text-amber-800',
        )}
      >
        <Mail className="size-3.5 shrink-0" aria-hidden />
        {completo ? (
          <span>
            {avisados} de {count} {count === 1 ? 'fornecedor avisado' : 'fornecedores avisados'} por
            e-mail.
          </span>
        ) : (
          <span>
            <strong className="font-medium">
              Só {avisados} de {count} fornecedores foram avisados.
            </strong>{' '}
            Os demais não têm contato cadastrado, ou o envio não chegou ao fim.
          </span>
        )}
        <Link to="/admin/emails" className="underline-offset-4 hover:underline">
          ver registro
        </Link>
        {isAdmin && !completo && (
          <button
            type="button"
            disabled={enviando}
            onClick={() => void reenviar()}
            className="ml-auto underline-offset-4 hover:underline disabled:opacity-50"
          >
            {enviando ? 'enviando…' : 'reenviar aos que faltam'}
          </button>
        )}
      </p>
      {erro && <Alert>{erro}</Alert>}
    </div>
  );
}
